/**
 * Sales Control › References switchboard config (Access frmCtrlSalesMainSubFormReferences
 * "Go to Page:" combo → 12 read-only datasheet pages). Column field keys MUST match the
 * backend SQL aliases in `sale_control_reference_pg._SALE_CONTROL_REFERENCE_SPECS` and the
 * baseline `sale_control_columns.py` (mode `ref-<pageKey>`).
 */
import type {
  ReferenceColumnConfig,
  ReferenceFilterKind,
  ReferencePageConfig,
  ReferenceValueKind,
} from '@/components/references/types'

export type SaleControlReferencePageKey =
  | 'ServiceInvoices'
  | 'VirtualWH'
  | 'EXCalendar'
  | 'ValueUpdates'
  | 'Annex'
  | 'Delivery'
  | 'ListAndValo'
  | 'DeliveryPlace'
  | 'FuturesAllocation'
  | 'Certification'
  | 'Fixation'
  | 'CP'

// Types unified vào module dùng chung `@/components/references` (Inbound/Outbound
// chung 1 vỏ). Giữ alias tên cũ để không phải sửa call-site.
export type SaleControlRefValueKind = ReferenceValueKind
export type SaleControlRefColumn = ReferenceColumnConfig
export type SaleControlRefPageConfig = ReferencePageConfig<SaleControlReferencePageKey>

// Order = Access combo `cbPreferencesList` RowSource order.
export const SALE_CONTROL_REFERENCE_PAGE_ORDER: SaleControlReferencePageKey[] = [
  'ServiceInvoices',
  'VirtualWH',
  'EXCalendar',
  'ValueUpdates',
  'Annex',
  'Delivery',
  'ListAndValo',
  'DeliveryPlace',
  'FuturesAllocation',
  'Certification',
  'Fixation',
  'CP',
]

/**
 * Filter mỗi page forward vào API (page không khai → không gửi + header disable).
 * VirtualWH/Delivery/FuturesAllocation lọc theo ngày; Annex/Fixation lọc theo
 * Shipment Period (ngày) + Is Finalized của HĐ bán.
 */
export const SALE_CONTROL_REFERENCE_PAGE_FILTERS: Partial<
  Record<SaleControlReferencePageKey, ReferenceFilterKind[]>
> = {
  VirtualWH: ['date'],
  Delivery: ['date'],
  FuturesAllocation: ['date'],
  Annex: ['date', 'finalized'],
  Fixation: ['date', 'finalized'],
}

const T = (field: string, headerName: string, extra: Partial<SaleControlRefColumn> = {}): SaleControlRefColumn => ({
  field,
  headerName,
  ...extra,
})
const N = (field: string, headerName: string, precision = 2): SaleControlRefColumn => ({
  field,
  headerName,
  kind: 'number',
  precision,
})
const D = (field: string, headerName: string): SaleControlRefColumn => ({ field, headerName, kind: 'date' })

const UPDATED_COLS: SaleControlRefColumn[] = [D('date_update', 'Updated Date'), T('person_update', 'Updated Person')]

// Fixation fix1..fix30 blocks (Date Fix N / Lots Fixed N / Level Fixed N).
const FIXATION_FIX_COLS: SaleControlRefColumn[] = Array.from({ length: 30 }, (_, i) => i + 1).flatMap((n) => [
  D(`fix${n}_date`, `Date Fix ${n}`),
  N(`fix${n}_lots_fixed`, `Lots Fixed ${n}`, 0),
  N(`fix${n}_level_fixed`, `Level Fixed ${n}`, 2),
])

export const SALE_CONTROL_REFERENCE_PAGE_CONFIGS: Record<SaleControlReferencePageKey, SaleControlRefPageConfig> = {
  ServiceInvoices: {
    pageKey: 'ServiceInvoices',
    label: 'Service Invoices',
    sourceForm: 'frmCtrlSalesQryInvoice_SuggestInvoice',
    sourceQuery: 'CtrlSalesQryInvoice_SuggestInvoice',
    // Thứ tự cột theo TabIndex của datasheet Access (form single-column, ColumnOrder
    // không set → TabIndex là thứ tự hiển thị). INV-Status ở giữa, KHÔNG ở cuối.
    columns: [
      T('service_code', 'ServiceCode'),
      T('service_company', 'ServiceCompany', { proper: true, wrap: true, width: 220 }),
      T('inv_no_merge', 'INV-No'),
      N('quantity', 'INV-Quantity'),
      N('price', 'INV-Price'),
      T('trang_thai_kiem_tra', 'INV-CheckingStatus'),
      D('ngay_hoa_don', 'INV-Date'),
      N('total_amount', 'TotalAmount (after tax)'),
      N('total_discount_amount', 'DiscountAmount'),
      N('total_amount_without_vat', 'TotalAmount'),
      N('total_vat_amount', 'VATAmount'),
      T('product_note', 'INV-Note', { wrap: true, width: 200 }),
      T('loai_hoa_don', 'INV-Type'),
      T('trang_thai_hoa_don', 'INV-Status'),
      T('phan_loai_chung_tu', 'VoucherType'),
      T('trang_thai_lap_chung_tu', 'VoucherStatus'),
      T('loai_tien', 'Currency'),
      T('refno_finance', 'VoucherNo'),
      T('inv_tab', 'INVTab'),
    ],
  },
  VirtualWH: {
    pageKey: 'VirtualWH',
    label: 'Virtual WH',
    sourceForm: 'frmCtrlSalesQryRefVirtualWH',
    sourceQuery: 'CtrlSalesQryRefVirtualWH',
    columns: [
      T('receipt_no', 'RDN No'),
      D('date_in', 'Date in'),
      T('do_no', 'DO No'),
      T('truck_no', 'Truck No'),
      T('supplier_code', 'From Supplier'),
      T('supplier_name', 'Supplier name', { proper: true, wrap: true, width: 200 }),
      T('client_code', 'To Client'),
      T('client_name', 'Client name', { proper: true, wrap: true, width: 200 }),
      T('warehouse_code', 'Warehouse Code'),
      T('warehouse_name', 'Warehouse Name', { wrap: true, width: 180 }),
      T('quality_code', 'Quality'),
      N('announced_quantity', 'Announced Quantity'),
      N('no_of_bag', 'No of Bag', 0),
      N('vgm_quantity_kg', 'VGM Quantiy'),
      N('mc', 'MC'),
      N('bb', 'BB'),
      N('brown', 'Brown'),
      N('cherry', 'Cherry'),
      N('overdry', 'Overdry'),
      N('fm', 'FM'),
      N('black', 'Black'),
      N('brokens', 'Brokens'),
      N('foreign_matter', 'Foreign'),
      N('insect', 'Insect'),
      N('immature', 'Immature'),
      N('old', 'Old'),
      N('mouldy', 'Mouldy'),
      N('white', 'White'),
      N('s18', 'S18'),
      N('s17', 'S17'),
      N('s16', 'S16'),
      N('s15', 'S15'),
      N('s14', 'S14'),
      N('s13', 'S13'),
      N('s12', 'S12'),
      N('under_s12', '<S12'),
      N('deduction_quantity_kg', 'Deduction (Kg)'),
      N('deduction_price_vnd', 'Deduction (VND)'),
      N('payment_quantity', 'Payment Quantity'),
      T('status', 'Status'),
      T('note', 'Note', { wrap: true, width: 200 }),
      N('deduction_other', 'Deduction Other'),
    ],
  },
  EXCalendar: {
    pageKey: 'EXCalendar',
    label: 'EX Calendar',
    sourceForm: 'frmCtrlSalesQryRefEXCalendar',
    sourceQuery: 'CtrlSalesQryExCalendar',
    // Bảng dữ liệu copy 1:1 từ /master-data/calendar (MasterDataCalendarView):
    // cùng cột / thứ tự / label và cùng nguồn v_market_calendar (các cột *_day
    // được COMPUTE, không NULL như base table). Year hiển thị integer thuần
    // (text, KHÔNG grouping "2,026") → align phải như calendar page.
    columns: [
      T('year', 'Year', { align: 'right' }),
      N('month', 'Month', 0),
      T('code', 'Code'),
      D('first_business_day', 'First Business Day'),
      D('last_business_day', 'Last Business Day'),
      D('first_notice_day', 'First Notice Day'),
      D('last_trading_day', 'Last Trading Day'),
      D('last_fixing_day', 'Last Fixing Day'),
      T('ex', 'Exchange'),
      T('ex_text', 'Exchange Text'),
      T('full_symbol', 'Full symbol'),
      T('updated_date', 'Updated Date', { kind: 'datetime' }),
      T('updated_by', 'Updated Person'),
    ],
  },
  ValueUpdates: {
    pageKey: 'ValueUpdates',
    label: 'Value Updates',
    sourceForm: 'frmCtrlSalesQryRefValueUpdate',
    sourceQuery: 'CtrlSalesQryValueUpdate',
    columns: [
      D('date', 'Date'),
      T('code', 'Code'),
      T('description', 'Description', { wrap: true, width: 220 }),
      { ...N('premium_vs_g2_5percent', 'Premium vs. G2 5%'), noTotal: true },
      T('reason_of_change', 'Reason of change', { wrap: true, width: 220 }),
      // Updated Date = COALESCE(updated_date, created_date) là timestamp → format
      // datetime DD-MMM-YYYY HH:mm (KHÔNG dùng UPDATED_COLS date-only chung).
      T('date_update', 'Updated Date', { kind: 'datetime' }),
      T('person_update', 'Updated Person'),
    ],
  },
  Annex: {
    pageKey: 'Annex',
    // Copy 1:1 bảng dữ liệu của /sales/contract/amendment (SaleAmendmentListView):
    // cùng cột / thứ tự / label / format. Label page đổi "Annex" → "Amendment".
    label: 'Amendment',
    sourceForm: 'frmCtrlSalesQryRefAnnex',
    sourceQuery: 'ContractSalesQryAnnex',
    columns: [
      // id: number căn phải nhưng KHÔNG cộng tổng (khớp NON_SUM_TOTAL_KEYS bên amendment).
      { ...N('id', 'ID', 0), noTotal: true },
      T('annex_no', 'Amendment No.'),
      D('annex_date', 'Amendment Date.'),
      T('contract_no', 'Contract Ref'),
      T('cp_code', 'Supplier Code'),
      T('name', 'Supplier', { proper: true, wrap: true, width: 200 }),
      T('content_before_change', 'Before change', { wrap: true, width: 360 }),
      T('content_after_change', 'After change', { wrap: true, width: 360 }),
      T('note', 'Note', { wrap: true, width: 300 }),
      N('price', 'Price'),
      N('quantity', 'Quantity'),
      // Updated Date = timestamp → datetime DD-MMM-YYYY HH:mm (như amendment page).
      T('date_update', 'Updated Date', { kind: 'datetime' }),
      T('person_update', 'Updated Person'),
    ],
  },
  Delivery: {
    pageKey: 'Delivery',
    label: 'Delivery',
    sourceForm: 'frmCtrlSalesQryRefDelivery',
    sourceQuery: 'CtrlSalesQryDelivery',
    // Thứ tự cột theo datasheet Access frmCtrlSalesQryRefDelivery (TabIndex) —
    // khớp baseline `ref-Delivery`. date_update/person_update nằm xen giữa (vị
    // trí 16/18), KHÔNG ở cuối → inline thay vì UPDATED_COLS.
    columns: [
      T('gdn_id', 'GDN ID'),
      N('bags_on_truck', 'Bags on truck', 0),
      D('date_out', 'Date out'),
      N('net_real_weight', 'Net real weight'),
      T('do_no', 'DO No.'),
      N('deduction_rate', 'Deduction rate'),
      T('from_warehouse', 'From warehouse', { wrap: true, width: 180 }),
      T('supervisor', 'Supervisor'),
      T('to_warehouse', 'To warehouse', { wrap: true, width: 180 }),
      N('bag_at_buyer', 'Bag at buyer', 0),
      T('lot_name_buyer', "Lot name's buyer", { wrap: true, width: 200 }),
      N('net_weight_at_buyer', 'Net weight at buyer'),
      T('lot_id', 'Lot ID'),
      N('deduction_at_buyer', 'Deduction at buyer'),
      T('stack_id', 'Stack ID'),
      D('date_update', 'Updated Date'),
      T('package', 'Package'),
      T('person_update', 'Updated Person'),
      N('number_theory_of_bag', 'Number theory of bag', 0),
      N('real_bags', 'Real bags', 0),
      T('truck_no', 'Truck No.'),
      T('package_to_truck', 'Package to Truck'),
      N('gross_weight', 'Gross Weight'),
    ],
  },
  ListAndValo: {
    pageKey: 'ListAndValo',
    label: 'List and Valo',
    sourceForm: 'frmCtrlSalesQryRefListAndValo',
    sourceQuery: 'CtrlSalesQryListAndValo',
    // Thứ tự cột theo datasheet Access frmCtrlSalesQryRefListAndValo (TabIndex) —
    // khớp baseline `ref-ListAndValo`. date_update/person_update xen giữa (vị trí
    // 8/10) → inline thay vì UPDATED_COLS. Format cột đặc biệt: ranked = số nguyên
    // (không phải text), premium_vs_g2 / valuation_in_use = số 4 chữ số thập phân
    // (như purchase ListAndValo). Updated Date = timestamp → datetime.
    columns: [
      T('code', 'Code'),
      T('special_requirements', 'Special Requirements', { wrap: true, width: 320 }),
      T('description', 'Description', { wrap: true, width: 320 }),
      T('note', 'Note', { wrap: true, width: 300 }),
      T('bean_size', 'Bean Size'),
      T('description_on_contract', 'Description On Contract', { wrap: true, width: 360 }),
      T('phase', 'Phase'),
      T('date_update', 'Updated Date', { kind: 'datetime' }),
      T('category', 'Category'),
      T('person_update', 'Updated Person'),
      N('ranked', 'Ranked', 0),
      N('premium_vs_g2', 'Premium vs G2', 4),
      N('valuation_in_use', 'Valuation in Use', 4),
    ],
  },
  DeliveryPlace: {
    pageKey: 'DeliveryPlace',
    label: 'Delivery Place',
    sourceForm: 'frmCtrlSalesQryRefDeliveryPlace',
    sourceQuery: 'CtrlSalesQryDeliveryPlace',
    columns: [
      // delivery_place là text dài → wrap (auto row height) + width mặc định rộng.
      T('delivery_place', 'Delivery Place', { wrap: true, width: 360 }),
      N('logistic_costs_to_fob', 'Logistic costs to FOB'),
      ...UPDATED_COLS,
    ],
  },
  FuturesAllocation: {
    pageKey: 'FuturesAllocation',
    label: 'Futures Allocation',
    sourceForm: 'frmCtrlSalesQryRefFuturesAllocation',
    sourceQuery: 'CtrlSalesQryFuturesAllocation',
    columns: [
      D('date', 'Date'),
      T('contract_id', 'Contract ID'),
      T('ps', 'P/S'),
      T('cp', 'CP'),
      N('kg', 'Kg'),
      T('type', 'Type'),
      N('lot_allocating', 'Lot allocating', 0),
      T('futures_id', 'Futures ID'),
      T('buysell', 'Buy/Sell'),
      T('month', 'Month'),
      N('price', 'Price'),
      N('balance', 'Balance'),
    ],
  },
  Certification: {
    pageKey: 'Certification',
    label: 'Certification',
    sourceForm: 'frmCtrlSalesQryRefCertification',
    sourceQuery: 'MdQryCertification',
    // Thứ tự cột khớp datasheet Access frmCtrlSalesQryRefCertification. Format cột
    // đặc biệt (như purchase Certification): premium = số 4 chữ số thập phân;
    // max_date_change = date; Updated Date = datetime (timestamp) → inline thay
    // UPDATED_COLS.
    columns: [
      T('certification', 'Certification'),
      N('premium', 'Premium (New)', 4),
      D('max_date_change', 'Date Change (New)'),
      T('date_update', 'Updated Date', { kind: 'datetime' }),
      T('person_update', 'Updated Person'),
    ],
  },
  Fixation: {
    pageKey: 'Fixation',
    label: 'PTBF Fixation (Sale)',
    sourceForm: 'frmCtrlSalesQryRefFixation',
    sourceQuery: 'ContractSalesQryPTBFFixation',
    // Thứ tự cột theo datasheet Access frmCtrlSalesQryRefFixation (TabIndex) —
    // khớp baseline `ref-Fixation`: upload_txt/contract_sale/release_date ở đầu,
    // txt_cus_sign trước khối fix. Số căn phải auto (kind number); mtons/ptbf_diffs/
    // final_price 2 chữ số thập phân, no_of_lots/balance_lots_tbf số nguyên.
    columns: [
      T('upload_txt', 'Is Uploaded?'),
      T('contract_sale', 'Contract No'),
      D('release_date', 'ReleaseDate'),
      T('buyers_ref', "Buyer's Ref"),
      T('cp_code', 'Client Ref'),
      T('name', 'Client Name', { proper: true, wrap: true, width: 260 }),
      T('quality', 'Grade'),
      T('description', 'Description', { wrap: true, width: 320 }),
      N('mtons', 'Mtons'),
      N('no_of_lots', 'No. of Lots', 0),
      N('ptbf_diffs', 'PTBF Diffs'),
      T('futures_month', 'Futures Month'),
      N('balance_lots_tbf', 'Balance Lots TBF', 0),
      N('final_price', 'Final Price'),
      T('txt_cus_sign', 'Customer Signed?'),
      ...FIXATION_FIX_COLS,
    ],
  },
  CP: {
    pageKey: 'CP',
    label: 'CP',
    sourceForm: 'frmCtrlSalesQryRefCP',
    sourceQuery: 'CtrlSalesQryCP',
    // Thứ tự cột khớp datasheet Access frmCtrlSalesQryRefCP. Caption code/name_cp
    // = "Counter Party ..." (list gồm cả KH lẫn NCC, cột Type phân loại).
    // Format cột đặc biệt: name_cp/
    // short_name/rep proper-case (DB lưu HOA); type_cp/status_cp là text computed
    // (Customer/Supplier, Valid/Invalid); Updated Date = datetime (timestamp).
    columns: [
      T('code', 'Counter Party Code'),
      T('name_cp', 'Counter Party Name', { proper: true, wrap: true, width: 220 }),
      T('short_name', 'Short Name', { proper: true, wrap: true, width: 180 }),
      T('address', 'Address', { wrap: true, width: 220 }),
      T('phone', 'Phone'),
      T('mobile', 'Mobile'),
      T('fax', 'Fax'),
      T('vat_code', 'VAT Code'),
      T('email', 'Email'),
      T('status', 'Status'),
      T('rep', 'Rep', { proper: true }),
      T('position', 'Position'),
      T('reg_no', 'Reg No'),
      T('issued_by', 'Issued By', { wrap: true, width: 200 }),
      D('issuing_date', 'Issuing Date'),
      T('id_no', 'ID No'),
      T('id_place', 'ID Place'),
      D('id_date', 'ID Date'),
      T('note', 'Note', { wrap: true, width: 200 }),
      T('sale_offshore_onshore', 'Sale Offshore Onshore'),
      T('type_cp', 'Type'),
      T('status_cp', 'Using'),
      T('date_update', 'Updated Date', { kind: 'datetime' }),
      T('person_update', 'Updated Person'),
    ],
  },
}
